// React
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Actions
import { register } from "../../../actions/userActions";

// Internal Components
import { ErrorMessage } from "../../../components/ui/Error/ErrorMessage";
import PasswordChecker from "../../../components/ui/PasswordChecker/PasswordChecker";
import HowItWorksModal from "../../../components/ui/HowItWorksModal/HowItWorksModal";

// Page Data
import {
  ARRIVAL_REASONS,
  JOURNAL_HABITS,
  DEMOGRAPHIC_PURPOSE,
} from "../../../constants/demographicOptions";

// External Components
import ReactGA from "react-ga4";

import Honeypot from "../../../components/ui/Honeypot/Honeypot";
import useFormToken from "../../../hooks/useFormToken";
import { HONEYPOT_FIELD } from "../../../constants/contactOptions";

export default function RegisterTest({ history }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userRegister = useSelector((state) => state.userRegister);
  const { loading, error, userInfo } = userRegister;

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscription, setSubscription] = useState({
    sessionId: "123",
    plan: "trial",
    customerId: "123",
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [terms, setTerms] = useState(false);
  // Wave 1 demographics. Both optional, both default to "" so an untouched
  // select is simply never sent — the failure mode that made the old
  // /register/profile screen collect nothing.
  const [arrivalReason, setArrivalReason] = useState("");
  const [journalHabit, setJournalHabit] = useState("");
  const [message, setMessage] = useState(null);
  const [ready, setReady] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Bot signals — invisible to a real user. See middlewares/botCheck.js.
  const [honeypot, setHoneypot] = useState("");
  const formToken = useFormToken();

  const createCustomer = async () => {
    try {
      const response = await fetch(
        "https://api.nowasteddays.io/create-customer",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            name: name,
          }),
        },
      );

      if (response.ok) {
        const customerData = await response.json();

        // Now you can access the customer ID
        const customerId = customerData.id;

        if (customerId && customerId !== "123") {
          setSubscription((prevSubscription) => ({
            ...prevSubscription,
            customerId: customerId,
          }));

          const demographics = {};
          if (arrivalReason) demographics.arrivalReason = arrivalReason;
          if (journalHabit) demographics.journalHabit = journalHabit;

          dispatch(
            register(
              name,
              email,
              password,
              { ...subscription, customerId },
              demographics,
              { [HONEYPOT_FIELD]: honeypot, formToken },
            ),
          );

          // Fired here rather than alongside sign_up below, so these counts
          // match the records actually created — the wave 1 denominator has to
          // reconcile with demographic.promptedAt on the user.
          // Behaviour, never content: whether the questions were answered, not
          // what was answered.
          ReactGA.event("demographics_prompted", { wave: 1 });
          if (arrivalReason || journalHabit) {
            ReactGA.event("demographics_completed", {
              wave: 1,
              fields_answered: [arrivalReason, journalHabit].filter(Boolean)
                .length,
            });
          } else {
            ReactGA.event("demographics_skipped", { wave: 1 });
          }
        }
        ReactGA.event("sign_up", {
          method: "Email", // Or "Google", "Facebook", etc.
        });
      }
    } catch (error) {
      console.log("Error creating customer:", error);
    }
  };

  useEffect(() => {
    if (userInfo) {
      localStorage.setItem("nwd_how_it_works_seen", "true");
      setShowHowItWorks(true);
    }
  }, [userInfo]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setMessage("Your passwords do not match.");
    } else {
      try {
        await createCustomer(); // Wait for createCustomer to complete
        // Add a delay or wait for the state to update
      } catch (error) {
        console.error("Error creating customer:", error);
      }
    }
  };

  const readyHandler = (bool) => {
    setReady(bool);
  };

  return (
    <>
      {showHowItWorks && (
        <HowItWorksModal mode="onboarding" onClose={() => navigate("/today")} />
      )}
      <section className="registerContainer">
        <div className="input--label">
          <label style={{ fontWeight: "bold", color: "#e2dfcf" }}>
            Register
          </label>
        </div>
        {/* <img
          className="sticker"
          src="https://cdn.sanity.io/images/ou6d2gec/production/088a4919740e18a8e075aea3319a06cbe5dfba5d-4500x4500.png"
          width="25%"
          alt="daily planner"
          loading="lazy"
        /> */}
        <form className="register form" onSubmit={submitHandler}>
          <div className="demographic__row--first">
            <label style={{ color: "#004a22" }} htmlFor="name">
              Your Name
            </label>
            <input
              id="name"
              className="formInput register"
              type="text"
              value={name}
              autoComplete="off"
              placeholder="Enter your name here"
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
          </div>
          <div className="demographic__row">
            <label style={{ color: "#004a22" }} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="formInput register"
              type="email"
              value={email}
              autoComplete="off"
              placeholder="Enter your email address"
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
              required
            />
          </div>
          <div className="demographic__row">
            <label style={{ color: "#004a22" }} htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="formInput register"
              type="password"
              value={password}
              autoComplete="off"
              placeholder="Create your password here"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="demographic__row">
            <label style={{ color: "#004a22" }} htmlFor="confPassword">
              Confirm Password
            </label>
            <input
              id="confPassword"
              className="formInput register"
              type="password"
              value={confirmPassword}
              autoComplete="off"
              placeholder="Confirm your password here"
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <div className="demographic__row">
            <label style={{ color: "#004a22" }} htmlFor="arrivalReason">
              What brought you here?{" "}
              <span className="demographic__optional">Optional</span>
            </label>
            <select
              id="arrivalReason"
              className="demographic__option"
              value={arrivalReason}
              onChange={(e) => setArrivalReason(e.target.value)}
            >
              <option value="">Choose one, or skip</option>
              {ARRIVAL_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
          <div className="demographic__row">
            <label style={{ color: "#004a22" }} htmlFor="journalHabit">
              Do you journal at the moment?{" "}
              <span className="demographic__optional">Optional</span>
            </label>
            <select
              id="journalHabit"
              className="demographic__option"
              value={journalHabit}
              onChange={(e) => setJournalHabit(e.target.value)}
            >
              <option value="">Choose one, or skip</option>
              {JOURNAL_HABITS.map((habit) => (
                <option key={habit} value={habit}>
                  {habit}
                </option>
              ))}
            </select>
            <p className="demographic__purpose">{DEMOGRAPHIC_PURPOSE}</p>
          </div>
          <Honeypot value={honeypot} onChange={setHoneypot} />
          <div className="demographic__row--row">
            {error && <ErrorMessage variant="danger">{error}</ErrorMessage>}
            {message && <ErrorMessage variant="danger">{message}</ErrorMessage>}
            {password && (
              <PasswordChecker
                password={password}
                confirmPassword={confirmPassword}
                readyHandler={readyHandler}
              />
            )}
            {ready ? (
              <button className="registerButton" type="submit">
                Register
              </button>
            ) : (
              <button
                className="registerButton"
                type="submit"
                disabled
                style={{ opacity: "0.5" }}
              >
                Register
              </button>
            )}
          </div>
          <span>
            By signing up, you agree to our{" "}
            <a href={"/terms-and-conditions"}>Terms of Use</a> and{" "}
            <a href={"/privacy-policy"}>Privacy Policy</a>
          </span>
          <span>
            If you have already signed up, click <a href="/login">here...</a>
          </span>
        </form>
      </section>
    </>
  );
}
